import { useState, useCallback, useEffect, useRef } from 'react';

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Сохраняем initialValue в ref — стабильная ссылка, не вызывает лишних ре-рендеров
  const initialValueRef = useRef(initialValue);

  const [stored, setStored] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValueRef.current;
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? (JSON.parse(item) as T) : initialValueRef.current;
    } catch { return initialValueRef.current; }
  });

  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStored((prev) => {
      const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value;
      try { window.localStorage.setItem(key, JSON.stringify(next)); } catch { /* silent */ }
      return next;
    });
  }, [key]);

  const removeValue = useCallback(() => {
    setStored(initialValueRef.current);
    try { window.localStorage.removeItem(key); } catch { /* silent */ }
  }, [key]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;
      try {
        setStored(e.newValue !== null
          ? (JSON.parse(e.newValue) as T)
          : initialValueRef.current);
      } catch { /* silent */ }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key]);

  return [stored, setValue, removeValue];
}
